import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { tap, map, Observable, of, throwError } from 'rxjs';
import { retry, catchError, concatMap } from 'rxjs/operators';

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'

import { DocListEntry } from 'src/app/shared/doc-list-entry';
import { DocumentListFilter } from 'src/app/shared/document-list-filter';
import { Currency, CurrencyRate, CurrencyRateCreated, CurrencyRateListItem, CurrencySaved, CurrencyUpdated, EditCurrencyResult } from 'src/app/shared/currency';
import { CurrencyPartialSaveError } from 'src/app/shared/currency-save-error';
import { DailyGraphData, CrossRateGraphData } from 'src/app/shared/dashboard';
import { PrintReportSetting } from 'src/app/shared/global-settings-data';

/**
 * Query parameters accepted by the currency rates list endpoint.
 * All fields are optional — omitted fields produce no filter on that dimension.
 */
export class crListFetchParams {
  st?: number;    // Start from record
  ft?: number;    // Fetch number records
  fs?: any;       // Filter parameters string
  sp?: {          // Sorting parameters object
    sf: string;
    so: number;
  }
};

/**
 * REST client for currencies, currency rates, dashboard data, and print report
 * settings. Covers the following backend route groups:
 * `/Currency`, `/CurrencyRates`, `/dashboard/*`, `/Helper`, `/PrintReports`.
 *
 * Every request includes the standard Webyto headers via
 * {@link GetglobalsettingsService.getStandardApiHeaders}, resolved fresh on
 * each call so runtime Setup-screen overrides are picked up without a page
 * reload.
 *
 * All methods apply a single `retry(1)` on network failure and surface errors
 * via {@link handleError}, which currently shows a browser `alert`. This is a
 * known legacy pattern flagged for replacement with a toast notification in
 * Phase 1.
 */
@Injectable({
  providedIn: 'root'
})

export class RestApiService {

  apiURL: string = '';

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  /**
   * Builds the full API URL by prepending the runtime API server address.
   *
   * @param url - Path segment starting with `/` (e.g. `/Currency`).
   * @returns Absolute URL to the backend endpoint.
   */
  private _getApiUrl(url: string = ''): string {
    let _api: string = this.globalsettingsservice.getApiServerUrl();
    return _api + url;
  }

  /**
   * Revives `DocListEntry` date fields (raw strings over the wire, despite the
   * `Date` type annotation — JSON has no date type and `HttpClient` never
   * parses them back) into real `Date` objects, in place. Mirrors the
   * boundary revival `DocumentApiService.getDocumentById` does for `Document`.
   */
  private _reviveDocListEntryDates(d: DocListEntry): DocListEntry {
    if (d.DOCDATE) { d.DOCDATE = new Date(d.DOCDATE); }
    if (d.DOCEXPDATE) { d.DOCEXPDATE = new Date(d.DOCEXPDATE); }
    if (d.DOCVALDATE) { d.DOCVALDATE = new Date(d.DOCVALDATE); }
    if (d.PAIDNOWDATE) { d.PAIDNOWDATE = new Date(d.PAIDNOWDATE); }
    return d;
  }

  /** Array form of {@link _reviveDocListEntryDates}, applied in place to every row. */
  private _reviveDocListEntryArrayDates(data: DocListEntry[]): DocListEntry[] {
    data.forEach(d => this._reviveDocListEntryDates(d));
    return data;
  }

  // ---------------------------------------------------------------------------
  // Currency
  // ---------------------------------------------------------------------------

  /**
   * Returns all currencies with their exchange rates as of the given date.
   *
   * Calls `GET /Currency/?_date=<fromDate>`.
   *
   * @param fromDate - Reference date for rate lookup. Defaults to today.
   * @returns Observable emitting an array of `Currency` records.
   */
  getCurrency(fromDate: Date = new Date()): Observable<Currency[]> {
    if (!fromDate) { fromDate = new Date(); }
    return this.http.get<Currency[]>(this._getApiUrl('/Currency/?_date=' + fromDate.toISOString()), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns all currencies with their exchange rates included in each record.
   *
   * Calls `GET /Currency/withRate?_date=<fromDate>`.
   *
   * @param fromDate - Reference date for rate lookup. Defaults to today.
   * @returns Observable emitting an array of `Currency` records with rates.
   */
  getCurrencyWithRate(fromDate: Date = new Date()): Observable<Currency[]> {
    if (!fromDate) { fromDate = new Date(); }
    return this.http.get<Currency[]>(this._getApiUrl('/Currency/withRate?_date=' + fromDate.toISOString()), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
    }

  /**
   * Returns detailed information for a single currency by its ID, including
   * the rate as of the given date.
   *
   * Calls `GET /Currency/:id/Info?_date=<fromDate>`.
   *
   * @param id - `CURID` primary key of the currency.
   * @param fromDate - Reference date for rate lookup. Defaults to today.
   * @returns Observable emitting a one-element `Currency` array.
   */
  getCurrencyInfoByID(id: number, fromDate: Date = new Date()): Observable<Currency[]> {
    if (!fromDate) { fromDate = new Date(); }
    return this.http.get<Currency[]>(this._getApiUrl('/Currency/'+id+'/Info?_date=' + fromDate.toISOString()), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
    }

  // ---------------------------------------------------------------------------
  // Currency rates
  // ---------------------------------------------------------------------------

  /**
   * Creates a new currency rate record.
   *
   * Calls `POST /CurrencyRates/0`.
   *
   * No `retry()` here, unlike this service's other methods (see the class
   * doc comment) -- this is a non-idempotent INSERT. A retry after the
   * response is lost but the server already committed the row silently
   * inserts a second, duplicate `CrossRates` row; this was one of the
   * confirmed contributors to the exact-duplicate rows `SQL-Database-
   * migration`'s `V021` cleaned up (2026-09-14, 182 rows removed).
   *
   * @param crv - The rate item to create.
   * @returns Observable emitting the new record's `CRID` primary key.
   */
  postCurrencyRates(crv: CurrencyRateListItem): Observable<{ 'CRID': number }> {
    return this.http.post<{ 'CRID': number }>(this._getApiUrl('/CurrencyRates/' + 0), crv, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        catchError(this.handleError)
      )
  }

  /**
   * Creates a rate row -- and, when `crv.CURID` is not a real id, the currency itself.
   *
   * Calls `POST /CurrencyRates/0`. With `CURID` null/0 the API requires `CURCODE` + `CURNAME`
   * and inserts the `Currencies` row and the first rate in one transaction.
   *
   * Unlike `postCurrencyRates` this does NOT go through `handleError` (a `window.alert` with
   * only the status line): the caller needs the `409` "code already exists" / `400` text from
   * the response body to show inside the dialog, so the `HttpErrorResponse` is rethrown as is.
   * No `retry()` -- non-idempotent INSERT, same reason as `postCurrencyRates`.
   *
   * @param crv - Rate to create; `CURCODE`/`CURNAME` are only read when `CURID` is null/0.
   * @returns Observable emitting `{ CRID, CURID }` of the created rows.
   */
  createCurrencyRate(crv: Pick<CurrencyRateListItem, 'CURID' | 'CURCODE' | 'CURNAME' | 'CRDATE' | 'CRVALUE'>): Observable<CurrencyRateCreated> {
    // Send exactly the API's contract, not the whole object: the dialog's UI-only flags (`selectAfter`, `renamed`,
    // `saveRate`) stay here, and `CURCODE`/`CURNAME` go along only when the currency is being created.
    const isNew = !(crv.CURID > 0);
    const body: Partial<CurrencyRateListItem> = { CRID: 0, CURID: crv.CURID, CRDATE: crv.CRDATE, CRVALUE: crv.CRVALUE };
    if (isNew) { body.CURCODE = crv.CURCODE; body.CURNAME = crv.CURNAME; }
    return this.http.post<CurrencyRateCreated>(this._getApiUrl('/CurrencyRates/' + 0), body, { headers: this.globalsettingsservice.getStandardApiHeaders() });
  }

  /**
   * Renames a currency: changes its `CURCODE`, `CURNAME` and/or `APINAME` (only the fields sent change).
   *
   * Calls `PUT /Currency/:id`. The API refuses (`409`) a code that another currency already has and a
   * code change for a currency that is a login's main or balance currency, `404` for an unknown id.
   *
   * Like `createCurrencyRate` it does not go through `handleError` -- the dialog shows the API's own
   * `{ error }` text -- and has no `retry()`.
   *
   * @param id - `CURID` of the currency to rename.
   * @param body - The fields to change.
   * @returns Observable emitting the currency as now stored.
   */
  updateCurrency(id: number, body: { CURCODE?: string; CURNAME?: string; APINAME?: string | null }): Observable<CurrencyUpdated> {
    return this.http.put<CurrencyUpdated>(this._getApiUrl('/Currency/' + id), body, { headers: this.globalsettingsservice.getStandardApiHeaders() });
  }

  /**
   * Saves what `EditCurrencyDlgComponent` confirmed, in the order that is safe to retry:
   * - new currency (`CURID` null/0): one `POST /CurrencyRates/0`, which creates the currency with its first rate;
   * - existing currency: `PUT /Currency/:id` first if it was renamed (idempotent), then `POST /CurrencyRates/0`
   *   if a rate was entered. If the rename succeeded but the rate failed, the error is a `CurrencyPartialSaveError`.
   *
   * @param e - The dialog's result.
   * @returns Observable emitting what was saved (`CURID` is always the currency's id).
   */
  saveCurrencyEdit(e: EditCurrencyResult): Observable<CurrencySaved> {
    if (!(e.CURID > 0)) {
      return this.createCurrencyRate(e).pipe(
        map(r => ({ CURID: r.CURID, CRID: r.CRID, renamed: false, rateSaved: true }))
      );
    }

    const rename$: Observable<unknown> = e.renamed ? this.updateCurrency(e.CURID, { CURCODE: e.CURCODE, CURNAME: e.CURNAME }) : of(null);
    return rename$.pipe(
      concatMap(() => {
        if (!e.saveRate) {
          return of<CurrencySaved>({ CURID: e.CURID, CRID: null, renamed: e.renamed, rateSaved: false });
        }
        return this.createCurrencyRate(e).pipe(
          map(r => ({ CURID: e.CURID, CRID: r.CRID, renamed: e.renamed, rateSaved: true })),
          catchError(err => throwError(() => e.renamed ? new CurrencyPartialSaveError(err) : err))
        );
      })
    );
  }

  /**
   * Updates an existing currency rate record.
   *
   * Calls `PUT /CurrencyRates/:CRID`.
   *
   * @param crv - The rate item to update. Must have a valid `CRID`.
   * @returns Observable emitting the number of rows affected.
   */
  putCurrencyRates(crv: CurrencyRateListItem): Observable<number> {
    return this.http.put<number>(this._getApiUrl('/CurrencyRates/' + crv.CRID), crv, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a single currency rate record by its primary key.
   *
   * Calls `GET /CurrencyRates/:row`.
   *
   * @param row - `CRID` primary key of the rate record.
   * @returns Observable emitting a one-element `CurrencyRateListItem` array.
   */
  getCurrencyRateByRowID(row: number): Observable<CurrencyRateListItem[]> {
    return this.http.get<CurrencyRateListItem[]>(this._getApiUrl('/CurrencyRates/' + row), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Deletes a single currency rate record by its primary key.
   *
   * Calls `DELETE /CurrencyRates/:row`.
   *
   * @param row - `CRID` primary key of the rate record to delete.
   * @returns Observable emitting the number of rows affected.
   */
  deleteCurrencyRateRowById(row: number): Observable<number> {
    return this.http.delete<number>(this._getApiUrl('/CurrencyRates/' + row), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a paginated and optionally filtered/sorted list of currency rate
   * records.
   *
   * Calls `GET /CurrencyRates` with query parameters derived from `options`.
   *
   * @param options - Pagination, filter and sort parameters. See
   *   {@link crListFetchParams} for field descriptions.
   * @returns Observable emitting an array of `CurrencyRateListItem` records.
   */
  getCurrencyRates(options: any): Observable<CurrencyRateListItem[]> {
    let queryString = '?'
      + (options.st ? ('&_start=' + options.st) : '')
      + (options.ft ? ('&_end=' + (options.st + options.ft)) : '')
      + ((options.sp && options.sp.sf) ? ('&_sf=' + encodeURIComponent(options.sp.sf)) : '')
      + ((options.sp && options.sp.so) ? ('&_so=' + encodeURIComponent(options.sp.so)) : '')
      + (options.fs ? ('&_flt=' + encodeURIComponent(JSON.stringify(options.fs))) : '')
      ;

    return this.http.get<CurrencyRateListItem[]>(this._getApiUrl('/CurrencyRates' + queryString), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns the most recent rate record for each currency.
   *
   * Calls `GET /CurrencyRates/Latest`.
   *
   * @returns Observable emitting an array of the latest `CurrencyRateListItem`
   *   records, one per currency.
   */
  getCurrencyLatestRates(): Observable<CurrencyRateListItem[]> {
    return this.http.get<CurrencyRateListItem[]>(this._getApiUrl('/CurrencyRates/Latest'), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Dashboard
  // ---------------------------------------------------------------------------

  /**
   * Returns historical cross-rate data for up to three currencies, used to
   * render the exchange rate chart on the home dashboard.
   *
   * Calls `GET /dashboard/currencyrategraph/:depth/:cid1/:cid2/:cid3`.
   *
   * @param fromDate - Start date for the graph range.
   * @param depth - Number of calendar days to look back.
   * @param cid1 - Primary currency ID.
   * @param cid2 - Secondary currency ID.
   * @param cid3 - Tertiary currency ID.
   * @returns Observable emitting an array of `CrossRateGraphData` points.
   */
  getCrossRateGraphData(fromDate, depth: number, cid1: number, cid2: number, cid3: number): Observable<CrossRateGraphData[]> {
    return this.http.get<CrossRateGraphData[]>(this._getApiUrl('/dashboard/currencyrategraph/' + depth + '/' + cid1 + '/' + cid2 + '/' + cid3 + '?_fromDate=' + fromDate.toISOString()), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns daily incoming payment totals for the dashboard payments chart.
   *
   * Calls `GET /dashboard/dailypayments/:company/:interval/:depth/:cid1`.
   *
   * @param company - Main company ID.
   * @param fromDate - Reference end date for the range.
   * @param depth - Number of calendar days to look back.
   * @param interval - Aggregation interval in days.
   * @param cid1 - Currency ID for amount conversion.
   * @param doin - Direction flag: 1 = incoming, 0 = outgoing.
   * @returns Observable emitting an array of `DailyGraphData` points.
   */
  getDailyPaymentsGraphData(company: number, fromDate: Date, depth: number, interval: number, cid1: number, doin: number): Observable<DailyGraphData[]> {
    return this.http.get<DailyGraphData[]>(this._getApiUrl('/dashboard/dailypayments/' + company + '/' + interval + '/' + depth + '/' + cid1 + '?_fromDate=' + fromDate.toISOString() + '&_doin='+doin), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Subtracts `n` business days from `d`, skipping Saturday and Sunday.
   * Used to convert a calendar-day depth into a business-day depth for
   * dashboard queries.
   *
   * @param d - Reference date.
   * @param n - Number of business days to subtract.
   * @returns The resulting date after skipping weekends.
   */
  private _addBusinessDays(d: Date, n: number): Date {
    let nd: Date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    let rd: Date = new Date( nd);
    rd.setDate(nd.getDate() - n);

    switch (rd.getDay()) {
      case 0:
        // Sunday — subtract 2 more days to land on Friday
        rd.setDate(rd.getDate() - 2);
        break;

      case 6:
        // Saturday — subtract 1 more day to land on Friday
        rd.setDate(rd.getDate() - 1);
        break;

      default:
        break;
    }
    return rd;
  }

  /**
   * Returns the absolute number of whole calendar days between two dates.
   *
   * @param startDate - First boundary date.
   * @param endDate - Second boundary date.
   * @returns Non-negative integer count of days.
   */
  private _daysBetween(startDate: Date, endDate: Date): number {
    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    const diffInMs = Math.abs(startDate.valueOf() - endDate.valueOf());
    return Math.trunc(diffInMs / millisecondsPerDay);
  }

  /**
   * Returns the top paying clients by total payment amount for the dashboard.
   *
   * Calls `GET /dashboard/monthlypaymentsbyclients/:company/:curid/:top`.
   *
   * @param company - Main company ID.
   * @param curid - Currency ID for amount conversion.
   * @param top - Maximum number of clients to return.
   * @param fromDate - Start date for the aggregation period.
   * @returns Observable emitting client payment summary records.
   */
  getMonthlyClientsPayments(company: number, curid: number, top: number, fromDate: Date): Observable<any> {
    return this.http.get<any>(this._getApiUrl('/dashboard/monthlypaymentsbyclients/' + company + '/' + curid + '/' + top + '/?_fromDate=' + fromDate.toISOString()), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns orders that have been received but not yet paid, within a rolling
   * business-day window ending at `fromDate`.
   *
   * Calls `GET /dashboard/notpaidorders/:company/:depth`.
   * The calendar `depth` is converted to business days before the request.
   * Each record receives a computed `PARTNERNAMESTR` combining the partner's
   * full name and alias when they differ.
   *
   * @param company - Main company ID.
   * @param depth - Look-back window in business days.
   * @param fromDate - Reference end date for the window.
   * @returns Observable emitting an array of `DocListEntry` records.
   */
  getNotPaidOrdersList(company: number, depth: number, fromDate: Date): Observable<DocListEntry[]> {
    let newdepth: number = this._daysBetween(this._addBusinessDays(fromDate, depth), fromDate);
    return this.http.get<DocListEntry[]>(this._getApiUrl( '/dashboard/notpaidorders/' + company + '/' + newdepth + '?_fromDate=' + fromDate.toISOString()), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        map((data: DocListEntry[]) => {
          data.forEach(dc => dc.PARTNERNAMESTR = (dc.DOCCLIENTSID == dc.DOCPCOCLIID) ? dc.PARTNERNAME : dc.PARTNERNIKNAME + ' (' + dc.PARTNERNAME + ')');
          return this._reviveDocListEntryArrayDates(data);
        }),
        catchError(this.handleError)
      )
  }

  /**
   * Returns orders that have been paid but not yet fully shipped, within a
   * rolling business-day window ending at `fromDate`.
   *
   * Calls `GET /dashboard/notshippedpaidorders/:company/:depth`.
   * Same business-day conversion and `PARTNERNAMESTR` logic as
   * {@link getNotPaidOrdersList}.
   *
   * @param company - Main company ID.
   * @param depth - Look-back window in business days.
   * @param fromDate - Reference end date for the window.
   * @returns Observable emitting an array of `DocListEntry` records.
   */
  getNotShippedOrdersList(company: number, depth: number, fromDate: Date): Observable<DocListEntry[]> {
    let newdepth: number = this._daysBetween(this._addBusinessDays(fromDate, depth), fromDate);
    return this.http.get<DocListEntry[]>(this._getApiUrl('/dashboard/notshippedpaidorders/' + company + '/' + newdepth + '?_fromDate=' + fromDate.toISOString()), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        map((data: DocListEntry[]) => {
          data.forEach(dc => dc.PARTNERNAMESTR = (dc.DOCCLIENTSID == dc.DOCPCOCLIID) ? dc.PARTNERNAME : dc.PARTNERNIKNAME + ' (' + dc.PARTNERNAME + ')');
          return this._reviveDocListEntryArrayDates(data);
        }),
        catchError(this.handleError)
      )
  }

  /**
   * Returns orders that were paid on or near `fromDate`, within a rolling
   * business-day window.
   *
   * Calls `GET /dashboard/paidorders/:company/:depth`.
   * Same business-day conversion and `PARTNERNAMESTR` logic as
   * {@link getNotPaidOrdersList}.
   *
   * @param company - Main company ID.
   * @param depth - Look-back window in business days.
   * @param fromDate - Reference end date for the window.
   * @returns Observable emitting an array of `DocListEntry` records.
   */
  getDailyPaidOrdersList(company: number, depth: number, fromDate: Date): Observable<DocListEntry[]> {
    let newdepth: number = this._daysBetween(this._addBusinessDays(fromDate, depth), fromDate);
    return this.http.get<DocListEntry[]>(this._getApiUrl('/dashboard/paidorders/' + company + '/' + newdepth + '?_fromDate=' + fromDate.toISOString()), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        map((data: DocListEntry[]) => {
          data.forEach(dc => dc.PARTNERNAMESTR = (dc.DOCCLIENTSID == dc.DOCPCOCLIID) ? dc.PARTNERNAME : dc.PARTNERNIKNAME + ' (' + dc.PARTNERNAME + ')');
          return this._reviveDocListEntryArrayDates(data);
        }),
        catchError(this.handleError)
      )
  }

  /**
   * Returns recently created orders within a rolling business-day window.
   *
   * Calls `GET /dashboard/recentorders/:company/:depth?_extended=<0|1>`.
   * Same business-day conversion and `PARTNERNAMESTR` logic as
   * {@link getNotPaidOrdersList}.
   *
   * @param company - Main company ID.
   * @param depth - Look-back window in business days.
   * @param fromDate - Reference end date for the window.
   * @param extend - When `true`, requests extended fields from the backend.
   * @returns Observable emitting an array of `DocListEntry` records.
   */
  getRecentOrdersList(company: number, depth: number, fromDate: Date, extend: boolean): Observable<DocListEntry[]> {
    let newdepth: number = this._daysBetween(this._addBusinessDays(fromDate, depth), fromDate);
    return this.http.get<DocListEntry[]>(this._getApiUrl('/dashboard/recentorders/' + company + '/' + newdepth + '?_fromDate=' + fromDate.toISOString() + '&_extended=' + ((extend) ? 1 : 0)), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        tap(data => { if (!environment.production) console.table(data); }),
        map((data: DocListEntry[]) => {
          data.forEach(dc => dc.PARTNERNAMESTR = (dc.DOCCLIENTSID == dc.DOCPCOCLIID) ? dc.PARTNERNAME : dc.PARTNERNIKNAME + ' (' + dc.PARTNERNAME + ')');
          return this._reviveDocListEntryArrayDates(data);
        }),
        catchError(this.handleError)
      )
  }

  /**
   * Returns a filtered, optionally sorted and paginated list of document entries.
   *
   * Calls `GET /Document/FindListByParameters` with query parameters derived from
   * `filters`, `sort`, and `range`. Each document record receives a computed
   * `PARTNERNAMESTR` combining the partner's full name and alias when they differ.
   *
   * @param filters - Array of `DocumentListFilter` constraints. At least one is required.
   * @param sort - Optional sort specification with field name and direction (1 = asc, -1 = desc).
   * @param range - Optional pagination range with start and end indices.
   * @returns Observable emitting an array of `DocListEntry` records.
   */
  getDocumentListByParameters(
    filters: DocumentListFilter[],
    sort?: { field: string; dir: 1 | -1 },
    range?: { start: number; end: number }
  ): Observable<DocListEntry[]> {
    let queryString = '';
    if (filters && filters.length > 0) {
      queryString += '?_filter=' + encodeURIComponent(JSON.stringify(filters));
    }
    if (sort) {
      queryString += (queryString ? '&' : '?') + '_sort=' + encodeURIComponent(sort.field) + '&_sortDir=' + sort.dir;
    }
    if (range) {
      queryString += (queryString ? '&' : '?') + '_start=' + range.start + '&_end=' + range.end;
    }

    return this.http.get<DocListEntry[]>(this._getApiUrl('/Document/FindListByParameters' + queryString), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        tap(data => { if (!environment.production) console.table(data); }),
        map((data: DocListEntry[]) => {
          data.forEach(dc => dc.PARTNERNAMESTR = (dc.DOCCLIENTSID == dc.DOCPCOCLIID) ? dc.PARTNERNAME : dc.PARTNERNIKNAME + ' (' + dc.PARTNERNAME + ')');
          return this._reviveDocListEntryArrayDates(data);
        }),
        catchError(this.handleError)
      )
  }

  /**
   * Returns orders that are still open and valid (not cancelled, not fully
   * shipped), within a rolling business-day window.
   *
   * Calls `GET /dashboard/validorders/:company/:depth?_extended=<0|1>`.
   * Same business-day conversion and `PARTNERNAMESTR` logic as
   * {@link getNotPaidOrdersList}.
   *
   * @param company - Main company ID.
   * @param depth - Look-back window in business days.
   * @param fromDate - Reference end date for the window.
   * @param extend - When `true`, requests extended fields from the backend.
   * @returns Observable emitting an array of `DocListEntry` records.
   */
  getValidOrdersList(company: number, depth: number, fromDate: Date, extend: boolean): Observable<DocListEntry[]> {
    let newdepth: number = this._daysBetween(this._addBusinessDays(fromDate, depth), fromDate);
    return this.http.get<DocListEntry[]>(this._getApiUrl('/dashboard/validorders/' + company + '/' + newdepth + '?_fromDate=' + fromDate.toISOString() + '&_extended=' + ((extend) ? 1 : 0)), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        map((data: DocListEntry[]) => {
          data.forEach(dc => dc.PARTNERNAMESTR = (dc.DOCCLIENTSID == dc.DOCPCOCLIID) ? dc.PARTNERNAME : dc.PARTNERNIKNAME + ' (' + dc.PARTNERNAME + ')');
          return this._reviveDocListEntryArrayDates(data);
        }),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Helper utilities
  // ---------------------------------------------------------------------------

  /**
   * Converts a number to its Ukrainian written-word representation.
   *
   * Calls `GET /Helper/NumberToWords?_number=<num>`. Used when generating
   * payment documents that require the amount written in words.
   *
   * @param num - The numeric value to convert.
   * @returns Observable emitting the server's string response.
   */
  getNumberByWords(num: number ): Observable<any> {
    return this.http.get<any>(this._getApiUrl('/Helper/NumberToWords?_number=' + num), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Print report settings
  // ---------------------------------------------------------------------------

  /**
   * Returns a specific print report template by company and report ID.
   *
   * Calls `GET /PrintReports/:cliid/:repid`. The company ID is taken from the
   * active user profile via {@link GetglobalsettingsService.getMainCompanyId}.
   *
   * @param repid - Report template ID.
   * @returns Observable emitting a one-element `PrintReportSetting` array.
   */
  getPrintReportsById( repid: number): Observable<PrintReportSetting[]> {
    let cliid = this.globalsettingsservice.getMainCompanyId();
    return this.http.get<PrintReportSetting[]>(this._getApiUrl('/PrintReports/' + cliid + '/' + repid), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns all print report templates available for the main company.
   *
   * Calls `GET /PrintReports/:cliid`.
   *
   * @returns Observable emitting an array of `PrintReportSetting` records.
   */
  getPrintReportsList(): Observable<PrintReportSetting[]> {
    let cliid = this.globalsettingsservice.getMainCompanyId();
    return this.http.get<PrintReportSetting[]>(this._getApiUrl('/PrintReports/' + cliid), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Returns print report templates filtered by document type.
   *
   * Calls `GET /PrintReports/:cliid/Type/:type`.
   *
   * @param type - Document type ID (`DOCTYPEID`) to filter by.
   * @returns Observable emitting matching `PrintReportSetting` records.
   */
  getPrintReportsByDocType( type: number): Observable<PrintReportSetting[]> {
    let cliid = this.globalsettingsservice.getMainCompanyId();
    return this.http.get<PrintReportSetting[]>(this._getApiUrl('/PrintReports/' + cliid + '/type/' + type), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Creates a new print report template record.
   *
   * Calls `POST /PrintReports/:cliid/:repid`.
   *
   * @param rep - The report setting to create. `CLIID` and `REPORTID` must be
   *   set on the object.
   * @returns Observable emitting the new record's `REPORTID`.
   */
  postPrintReport(rep: PrintReportSetting): Observable<{ 'REPORTID': number }> {
    return this.http.post<{ 'REPORTID': number }>(this._getApiUrl('/PrintReports/' + rep.CLIID + '/' + rep.REPORTID), rep, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Updates an existing print report template record.
   *
   * Calls `PUT /PrintReports/:cliid/:repid`.
   *
   * @param rep - The report setting to update. `CLIID` and `REPORTID` must be
   *   set on the object.
   * @returns Observable emitting the number of rows affected.
   */
  putPrintReport(rep: PrintReportSetting): Observable<number> {
    return this.http.put<number>(this._getApiUrl('/PrintReports/' + rep.CLIID + '/' + rep.REPORTID), rep, { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * @deprecated Not implemented — this is currently a guaranteed no-op, kept
   *   only so the one existing call site still compiles. It sends
   *   `DELETE /PrintReports/:cliid/0`, but `REPORTID` is an identity column
   *   (never `0`), so the backend's `WHERE CLIID=@CLIID AND REPORTID=@REPID`
   *   matches zero rows every time — no error, nothing deleted. There is no
   *   real bulk-delete route on the backend to call instead
   *   (`webyto_api-express4-TS-20250331/routes/PrintReports.ts` only has
   *   single-report `DELETE /:cliid/:repid`). Renamed from
   *   `deleteAllPrintReport` (PR-4, `api_parameter_alignment_V2.md`) so the
   *   name stops implying a working bulk delete exists. The caller
   *   (`edit-print-reports.component.ts`'s "Delete All" confirmation flow)
   *   still silently does nothing when clicked — that UX gap is unchanged
   *   by this rename and needs its own follow-up (either a real backend
   *   bulk-delete route, or a client-side fan-out over each report's own
   *   `deletePrintReportById`) before it's fixed for real.
   *
   * @returns Observable emitting the number of rows affected (always 0).
   */
  deleteAllPrintReportNotImplemented(): Observable<number> {
    let cliid = this.globalsettingsservice.getMainCompanyId();
    return this.http.delete<number>(this._getApiUrl('/PrintReports/' + cliid + '/' + 0), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  /**
   * Deletes a single print report template by its ID.
   *
   * Calls `DELETE /PrintReports/:cliid/:repid`.
   *
   * @param rep - The report setting to delete. `CLIID` and `REPORTID` must be
   *   set on the object.
   * @returns Observable emitting the number of rows affected.
   */
  deletePrintReportById(rep: PrintReportSetting): Observable<number> {
    return this.http.delete<number>(this._getApiUrl('/PrintReports/' + rep.CLIID + '/' + rep.REPORTID), { headers: this.globalsettingsservice.getStandardApiHeaders() })
      .pipe(
        retry(1),
        catchError(this.handleError)
      )
  }

  // ---------------------------------------------------------------------------
  // Error handling
  // ---------------------------------------------------------------------------

  /**
   * Centralised HTTP error handler used by all methods in this service.
   *
   * Extracts a human-readable message from the Angular `HttpErrorResponse`,
   * shows a browser `alert` (legacy pattern — Phase 1 will replace with a
   * toast), and re-throws the error so the calling Observable terminates.
   *
   * @param error - The Angular `HttpErrorResponse` or `ErrorEvent`.
   * @returns An Observable that immediately errors with the formatted message.
   */
  handleError(error) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    window.alert(errorMessage);
    return throwError(() => new Error(errorMessage));
  }
}
